import React, { useEffect, useState } from "react";
import { Table, Button, Modal, Form, Select, InputNumber, Upload, Space, message, Popconfirm, DatePicker, Tag } from "antd";
import { DownloadOutlined, DeleteOutlined, UploadOutlined, PlayCircleOutlined, TeamOutlined, DollarOutlined } from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import PageHeader from "../../components/ui/PageHeader";
import PageTransition from "../../components/ui/PageTransition";
import EmptyState from "../../components/ui/EmptyState";
import StatCard from "../../components/ui/StatCard";

const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Mirrors the backend's constants/currencies.ts — keep in sync if that
// list changes.
const CURRENCY_SYMBOLS = { PHP: "₱", USD: "$", INR: "₹", EUR: "€", GBP: "£", AUD: "A$", SGD: "S$", JPY: "¥", CNY: "¥", CAD: "C$" };
const currencySymbol = (code) => CURRENCY_SYMBOLS[code] || `${code} `;

const PayRun = () => {
  const { request } = useRequest();
  const [payslips, setPayslips] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [filters, setFilters] = useState({});
  const [period, setPeriod] = useState(moment());
  const [running, setRunning] = useState(false);

  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [form] = Form.useForm();

  const fetchPayslips = (params = {}) => {
    const query = new URLSearchParams(params).toString();
    request({
      url: `${apiPath.payRuns}${query ? `?${query}` : ""}`,
      method: "GET",
      onSuccess: (res) => setPayslips(res?.data?.data || []),
    });
  };

  const fetchLookups = () => {
    request({ url: apiPath.employees, method: "GET", onSuccess: (res) => setEmployees(res?.data?.employees || []) });
    request({ url: apiPath.departments, method: "GET", onSuccess: (res) => setDepartments(res?.data?.data || []) });
  };

  useEffect(() => {
    fetchLookups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const params = { month: period.month() + 1, year: period.year(), ...filters };
    fetchPayslips(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, filters]);

  const onFilterChange = (changed) => {
    const next = { ...filters, ...changed };
    Object.keys(next).forEach((k) => (next[k] === undefined || next[k] === "" || next[k] === null) && delete next[k]);
    setFilters(next);
  };

  const runPayroll = () => {
    const month = period.month() + 1;
    const year = period.year();
    setRunning(true);
    request({
      url: apiPath.payRunRun,
      method: "POST",
      data: { month, year },
      onSuccess: (res) => {
        setRunning(false);
        const { processed, totalNetPayUsd, totalOtPayUsd, anyFallbackRateUsed, skipped } = res?.data || {};
        message.success(`Pay run completed for ${monthNames[month - 1]} ${year}`);
        Modal.info({
          title: "Pay Run Summary",
          width: 520,
          content: (
            <div>
              <p>
                Processed <b>{processed || 0}</b> employee(s). Total net pay:{" "}
                <b>${(totalNetPayUsd || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</b>
                {totalOtPayUsd ? (
                  <>
                    {" "}
                    (incl. OT pay: <b>${totalOtPayUsd.toLocaleString(undefined, { minimumFractionDigits: 2 })}</b>)
                  </>
                ) : null}
              </p>
              <p style={{ color: "#8C8C8C", fontSize: 12 }}>
                Employees can be paid in different currencies — each was converted to USD at the live rate when this run
                executed.
                {anyFallbackRateUsed ? " Some used a fallback rate (live FX API was unreachable for that currency)." : ""}
              </p>
              {skipped && skipped.length > 0 && (
                <>
                  <p>Skipped:</p>
                  <ul>
                    {skipped.map((s, i) => (
                      <li key={i}>
                        {s.employeeId}: {s.reason}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          ),
        });
        fetchPayslips({ month, year, ...filters });
      },
      onError: (err) => {
        setRunning(false);
        message.error(err?.response?.data?.message || "Pay run failed");
      },
    });
  };

  const openUpload = (record) => {
    form.resetFields();
    form.setFieldsValue({ employee: record.employee?._id, month: record.month, year: record.year });
    setFileList([]);
    setUploadModalOpen(true);
  };

  const onSubmitUpload = () => {
    form.validateFields().then(async (values) => {
      if (fileList.length === 0) {
        message.error("Please select a PDF payslip file");
        return;
      }
      const formData = new FormData();
      formData.append("employee", values.employee);
      formData.append("month", values.month);
      formData.append("year", values.year);
      formData.append("file", fileList[0]);
      formData.append("replace", "true");

      const token = localStorage.getItem("i_ems_admin_token") || "";
      setUploading(true);
      try {
        await axios.post(`${apiPath.baseURL}${apiPath.payslips}`, formData, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
        });
        message.success("Signed payslip uploaded");
        setUploadModalOpen(false);
        fetchPayslips({ month: period.month() + 1, year: period.year(), ...filters });
      } catch (err) {
        message.error(err?.response?.data?.message || "Could not upload payslip");
      } finally {
        setUploading(false);
      }
    });
  };

  const onDownload = async (record) => {
    const token = localStorage.getItem("i_ems_admin_token") || "";
    try {
      const response = await axios.get(`${apiPath.baseURL}${apiPath.payslipDownload(record._id)}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", record.fileName || "payslip.pdf");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      message.error("Could not download payslip");
    }
  };

  const onDelete = (record) => {
    request({
      url: `${apiPath.payslips}/${record._id}`,
      method: "DELETE",
      onSuccess: () => {
        message.success("Payslip deleted");
        fetchPayslips({ month: period.month() + 1, year: period.year(), ...filters });
      },
    });
  };

  const totalNetPayUsd = payslips.reduce((sum, p) => sum + (p.netPayUsd || 0), 0);
  const totalOtPayUsd = payslips.reduce((sum, p) => sum + (p.otPayUsd || 0), 0);

  const columns = [
    { title: "Employee", dataIndex: ["employee", "name"] },
    { title: "Department", dataIndex: ["employee", "department", "name"] },
    {
      title: "Gross Salary",
      dataIndex: "grossSalary",
      render: (v, r) => `${currencySymbol(r.currency || "PHP")}${(v || 0).toLocaleString()}`,
    },
    {
      title: "Present / Absent",
      render: (_, r) => (
        <span>
          {r.presentDays ?? 0} / {r.absentDays ?? 0} (of {r.workingDays ?? 0})
        </span>
      ),
    },
    {
      title: "Deduction",
      dataIndex: "deductionAmount",
      render: (v, r) => `${currencySymbol(r.currency || "PHP")}${(v || 0).toLocaleString()}`,
    },
    { title: "OT Hours", dataIndex: "otHours", render: (v) => v || 0 },
    {
      title: "OT Pay",
      dataIndex: "otPay",
      render: (v, r) => `${currencySymbol(r.currency || "PHP")}${(v || 0).toLocaleString()}`,
    },
    {
      title: "Net Pay",
      dataIndex: "netPay",
      render: (v, r) => (
        <span>
          <b>${(r.netPayUsd ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</b>
          <br />
          <span style={{ color: "#8C8C8C", fontSize: 12 }}>
            {currencySymbol(r.currency || "PHP")}
            {(v || 0).toLocaleString()}
          </span>
        </span>
      ),
    },
    {
      title: "Signed PDF",
      render: (_, r) => (r.fileName ? <Tag color="green">Attached</Tag> : <Tag color="default">Not uploaded</Tag>),
    },
    {
      title: "Actions",
      render: (_, record) => (
        <Space>
          {record.fileName && <Button icon={<DownloadOutlined />} size="small" onClick={() => onDownload(record)} />}
          <Button size="small" icon={<UploadOutlined />} onClick={() => openUpload(record)}>
            {record.fileName ? "Replace" : "Upload signed copy"}
          </Button>
          <Popconfirm title="Delete this payslip?" onConfirm={() => onDelete(record)}>
            <Button icon={<DeleteOutlined />} size="small" danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <PageTransition>
      <PageHeader
        title="Pay Run"
        subtitle="Calculate payroll for a month and manage employee payslips"
        actions={
          <Space>
            <DatePicker picker="month" value={period} onChange={(v) => v && setPeriod(v)} allowClear={false} />
            <Button type="primary" icon={<PlayCircleOutlined />} loading={running} onClick={runPayroll}>
              Run Payroll
            </Button>
          </Space>
        }
      />

      <div className="i-ems-stat-row">
        <StatCard icon={<TeamOutlined />} label="Employees Paid" value={payslips.length} />
        <StatCard
          icon={<DollarOutlined />}
          label="Total Net Pay (USD)"
          display={`$${totalNetPayUsd.toLocaleString(undefined, { minimumFractionDigits: 2 })}`}
          color="var(--i-ems-success, #52c41a)"
        />
        <StatCard
          icon={<DollarOutlined />}
          label="Total OT Pay (USD)"
          display={`$${totalOtPayUsd.toLocaleString(undefined, { minimumFractionDigits: 2 })}`}
          color="var(--i-ems-warning, #faad14)"
        />
      </div>

      <Space style={{ marginBottom: 16 }} wrap>
        <Select
          allowClear
          placeholder="Employee"
          style={{ width: 180 }}
          options={employees.map((e) => ({ value: e._id, label: e.name }))}
          onChange={(v) => onFilterChange({ employee: v })}
        />
        <Select
          allowClear
          placeholder="Department"
          style={{ width: 180 }}
          options={departments.map((d) => ({ value: d._id, label: d.name }))}
          onChange={(v) => onFilterChange({ department: v })}
        />
      </Space>

      <Table
        rowKey="_id"
        columns={columns}
        dataSource={payslips}
        pagination={{ pageSize: 10 }}
        locale={{
          emptyText: (
            <EmptyState
              message={`No payslips for ${monthNames[period.month()]} ${period.year()} yet`}
              actionLabel="Run Payroll"
              onAction={runPayroll}
            />
          ),
        }}
      />

      <Modal
        title="Upload Signed Payslip"
        open={uploadModalOpen}
        onCancel={() => setUploadModalOpen(false)}
        onOk={onSubmitUpload}
        confirmLoading={uploading}
        destroyOnClose
      >
        <Form layout="vertical" form={form}>
          <Form.Item name="employee" label="Employee" rules={[{ required: true }]}>
            <Select
              showSearch
              optionFilterProp="label"
              disabled
              options={employees.map((e) => ({ value: e._id, label: `${e.name} (${e.employeeId})` }))}
            />
          </Form.Item>
          <Form.Item name="month" label="Month" rules={[{ required: true }]}>
            <Select disabled options={monthNames.map((m, i) => ({ value: i + 1, label: m }))} />
          </Form.Item>
          <Form.Item name="year" label="Year" rules={[{ required: true }]}>
            <InputNumber style={{ width: "100%" }} min={2000} max={2100} disabled />
          </Form.Item>
          <Form.Item label="Payslip PDF" required>
            <Upload
              accept="application/pdf"
              maxCount={1}
              fileList={fileList.map((f, i) => ({ uid: String(i), name: f.name, status: "done" }))}
              beforeUpload={(file) => {
                setFileList([file]);
                return false;
              }}
              onRemove={() => setFileList([])}
            >
              <Button icon={<UploadOutlined />}>Select PDF</Button>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>
    </PageTransition>
  );
};

export default PayRun;
